import { HStack, Text, Icon } from "@chakra-ui/react";
import { Tag } from "@chakra-ui/react/tag";
import { useTranslation } from "react-i18next";
import { Box } from "@chakra-ui/react/box";
import { AIModel } from "types";
import { VStack } from "@chakra-ui/react/stack";
import { Wrap, WrapItem } from "@chakra-ui/react/wrap";

export const ModelInfoCard = ({
  model,
  currencyPairs,
  getModelIcon,
  getModelCapabilityName,
}: {
  model: AIModel;
  currencyPairs: any;
  getModelIcon: (capability: string) => React.ElementType;
  getModelCapabilityName: (capability: string) => string;
}) => {
  const { t, i18n } = useTranslation();
  const showBrlPrice = i18n.language === "pt-BR";
  const isModelFree =
    (model.inputCostPer1M === 0 && model.outputCostPer1M === 0) ||
    model.costPerGeneration === 0;

  const getBrlPrice = (price: number) => {
    return price * currencyPairs.usd_brl;
  };

  const description =
    i18n.language === "pt-BR"
      ? model.description_ptBR || model.description
      : model.description;

  return (
    <Box
      key={model.id}
      p={4}
      borderWidth={1}
      borderRadius="md"
      borderColor="inherit"
      height="full"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "md",
        transition: "all 0.2s",
      }}
    >
      <VStack align="stretch" spacing={2}>
        <HStack justify="space-between">
          <HStack>
            <Icon
              as={getModelIcon(model.capabilities?.[0] || "")}
              boxSize={5}
              color="brand.primary"
            />
            <Text fontWeight="bold">{model.name}</Text>
          </HStack>
          <HStack>
            <Tag size="sm" colorScheme="purple">
              {model.provider}
            </Tag>
            {isModelFree && (
              <Tag size="sm" colorScheme="green">
                {t("chat.modelInfo.free")}
              </Tag>
            )}
          </HStack>
        </HStack>

        {model.description && (
          <Box>
            <HStack justify="space-between">
              <Text fontSize="sm" color="gray.500">
                {t("chat.modelInfo.description")}
              </Text>
              {!model.enabled && (
                <Tag size="sm" colorScheme="red" ml={2}>
                  {t("chat.modelInfo.disabled")}
                </Tag>
              )}
            </HStack>
            <Text fontSize="sm" noOfLines={3}>
              {description}
            </Text>
          </Box>
        )}
        {model.type === "llm" && (
          <>
            {/* <Box>
              <Text fontSize="sm" color="gray.500">
                {t("chat.modelInfo.maxOutputTokens")}
              </Text>
              <Text fontSize="sm">{model.maxOutputTokens?.toLocaleString()}</Text>
            </Box> */}
            <Box>
              <Text fontSize="sm" color="gray.500">
                {t("chat.modelInfo.inputCost")}
              </Text>
              <Text fontSize="sm">
                <Tag size="sm" colorScheme="blue">
                  ${model.inputCostPer1M?.toFixed(3)}
                </Tag>
                {showBrlPrice && (
                  <Tag size="sm" colorScheme="blue" ml={2}>
                    R$ {getBrlPrice(model.inputCostPer1M || 0).toFixed(2)}
                  </Tag>
                )}{" "}
                {t("chat.modelInfo.pricing.perMillion")}
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" color="gray.500">
                {t("chat.modelInfo.outputCost")}
              </Text>
              <Text fontSize="sm">
                <Tag size="sm" colorScheme="blue">
                  ${model.outputCostPer1M?.toFixed(3)}
                </Tag>
                {showBrlPrice && (
                  <Tag size="sm" colorScheme="blue" ml={2}>
                    R$ {getBrlPrice(model.outputCostPer1M || 0).toFixed(2)}
                  </Tag>
                )}{" "}
                {t("chat.modelInfo.pricing.perMillion")}
              </Text>
            </Box>
          </>
        )}
        {(model.type === "generation" || model.type === "reranker") && (
          <Box>
            <Text fontSize="sm" color="gray.500">
              {t("chat.modelInfo.costPerGeneration")}
            </Text>
            <Text fontSize="sm">
              <Tag size="sm" colorScheme="blue">
                ${model.costPerGeneration?.toFixed(3)}
              </Tag>

              {showBrlPrice && (
                <Tag size="sm" colorScheme="blue" ml={2}>
                  R$ {getBrlPrice(model.costPerGeneration || 0).toFixed(2)}
                </Tag>
              )}
            </Text>
          </Box>
        )}
        {/* // capabilities */}
        <Text fontSize="sm" color="gray.500">
          {t("chat.modelInfo.capabilities")}
        </Text>
        <Wrap spacing={2}>
          {model.capabilities?.map((capability) => (
            <WrapItem key={capability}>
              <Tag size="sm" colorScheme="gray">
                <Icon as={getModelIcon(capability)} boxSize={3} mr={1} />
                {getModelCapabilityName(capability)}
              </Tag>
            </WrapItem>
          ))}
        </Wrap>
      </VStack>
    </Box>
  );
};
